import React from 'react';
import classnames from 'classnames';
import styles from './domain-list-item.css';
import { LockIcon, UnlockIcon, WordpressIcon } from '../icons/icons';
import { Icon } from '../icon';
import Colors from '../colors';
import InlineStatus from '../alerts/inline-status';
import ListItem from './list-item';

interface Props {
  /** Url of the domain to display */
  domain: string;
  /** Number of pages the domain is being used by */
  pagesCount: number;
  /** Status of the domain */
  status?: 'success' | 'error' | 'warning';
  /**
   * Status message, when statusMessage is absent but status is present,
   * it will default to predefined domain statuses
   */
  statusMessage?: string;
  /** Whether the domain has an SSL certificate or not */
  secure?: boolean;
  /** Whether the domain is a Wordpress domain or not */
  wordpress?: boolean;
  /** Adds additional classes to the component. */
  className?: string;
  /** Contents of the flyout menu */
  flyoutMenu?: React.ReactElement<any>;
  /** Provides a data-testid */
  'data-testid'?: string;
}

const DomainListItem = (props: Props) => {
  const {
    domain,
    pagesCount,
    className,
    secure,
    wordpress,
    status,
    statusMessage,
    ...rest
  } = props;
  const domainClasses = classnames(styles.domain, className, {
    [styles.secure]: secure,
    [styles.wordpress]: wordpress
  });
  const wordpressIconSize = '34px';
  const icons = {
    wordpress: (
      <Icon color={Colors.GullGray_DEPRECATED} size={wordpressIconSize} className={styles.wpIcon}>
        <WordpressIcon />
      </Icon>
    ),
    secure: (
      <Icon color={Colors.White} className={styles.icon}>
        <LockIcon />
      </Icon>
    ),
    default: (
      <Icon color={Colors.White} className={styles.icon}>
        <UnlockIcon />
      </Icon>
    )
  };
  const statuses = {
    error: 'Error finding CNAME',
    success: 'Working and Secure',
    warning: 'Fetching Plugin Status...'
  };

  return (
    <ListItem className={domainClasses} {...rest}>
      <div className={styles.iconWrapper}>
        {wordpress ? icons.wordpress : secure ? icons.secure : icons.default}
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.title}>{domain}</div>
        <div className={styles.subtitle}>
          <span className={styles.strong}>
            {pagesCount} {pagesCount === 1 ? 'page ' : 'pages '}
          </span>
          {pagesCount === 1 ? 'is' : 'are'} using this domain
        </div>
      </div>
      {status && (
        <InlineStatus type={status} className={styles.status}>
          {statusMessage ? statusMessage : statuses[status]}
        </InlineStatus>
      )}
    </ListItem>
  );
};

DomainListItem.defaultProps = {
  secure: false,
  wordpress: false
};

export default DomainListItem;
